// Prédicats et regroupements PURS de la page Utilisateurs (aucune dépendance réseau ni React) :
// - une PERSONNE peut avoir plusieurs identités dans l'annuaire (Auth0 garde un enregistrement par
//   connexion non liée : e-mail / mot de passe ET Google sur la même adresse) ; la page les fusionne
//   par e-mail et la fiche « Gérer » agit sur toutes ;
// - l'IMPACT d'un compte (admin, peut mettre en production, contributeur) se lit dans les grants
//   expansés de ses rôles ; il alimente les filtres de la barre de recherche.
import type { PermissionCatalog } from "../../Auth/PermissionsContext";
import { expandGrants } from "../../Auth/permissionTokens";
import type { AdminUser, AssignableRole } from "./usersClient";

/** Une personne = toutes les identités partageant le même e-mail (insensible à la casse). */
export interface UserPerson {
	/** Clé stable : e-mail normalisé, sinon l'id de l'unique identité. */
	key: string;
	email: string;
	name: string;
	picture?: string | null;
	avatarKey?: string | null;
	/** Les identités, la plus récemment connectée d'abord ; la première est l'identité « principale ». */
	identities: AdminUser[];
	/** Union des rôles de toutes les identités (ordre d'apparition). */
	roles: string[];
	/** Vrai quand TOUTES les identités sont bloquées (la personne ne peut plus entrer du tout). */
	blocked: boolean;
	/** Vrai quand au moins une identité est bloquée. */
	anyBlocked: boolean;
	lastLogin?: string | null;
	createdAt?: string | null;
	lastPasswordReset?: string | null;
	/** Vrai si au moins une identité porte un mot de passe applicatif. */
	hasPassword: boolean;
	loginsCount?: number | null;
	/** IP de la dernière connexion connue (identité la plus récente qui en porte une). */
	lastIp?: string | null;
	/** E-mail vérifié : vrai dès qu'une identité l'affirme ; null si aucune ne le sait. */
	emailVerified?: boolean | null;
	source: string;
}

const normEmail = (e?: string | null) => (e ?? "").trim().toLowerCase();
const time = (iso?: string | null) => (iso ? new Date(iso).getTime() : 0);

/** Regroupe les identités par e-mail. Une identité sans e-mail reste seule. */
export function groupByPerson(users: AdminUser[]): UserPerson[] {
	const byKey = new Map<string, AdminUser[]>();
	for (const u of users) {
		const key = normEmail(u.email) || `id:${u.id}`;
		const list = byKey.get(key);
		if (list) list.push(u);
		else byKey.set(key, [u]);
	}
	const persons: UserPerson[] = [];
	for (const [key, list] of byKey) {
		const identities = [...list].sort((a, b) => time(b.lastLogin) - time(a.lastLogin) || (a.id < b.id ? -1 : 1));
		const primary = identities[0];
		const roles: string[] = [];
		const seen = new Set<string>();
		for (const u of identities) for (const r of u.roles ?? []) if (!seen.has(r)) { seen.add(r); roles.push(r); }
		const withName = identities.find((u) => (u.name ?? "").trim() && (u.name ?? "").trim() !== (u.email ?? "").trim());
		const withPicture = identities.find((u) => u.avatarKey) ?? identities.find((u) => u.picture);
		const logins = identities.map((u) => u.loginsCount).filter((n): n is number => typeof n === "number");
		const resets = identities.map((u) => u.lastPasswordReset).filter(Boolean) as string[];
		persons.push({
			key,
			email: primary.email,
			name: (withName?.name ?? primary.name ?? primary.email ?? "").trim() || primary.email,
			picture: withPicture?.picture ?? primary.picture ?? null,
			avatarKey: withPicture?.avatarKey ?? null,
			identities,
			roles,
			blocked: identities.every((u) => u.blocked),
			anyBlocked: identities.some((u) => u.blocked),
			lastLogin: identities.reduce<string | null>((best, u) => (time(u.lastLogin) > time(best) ? u.lastLogin ?? best : best), null),
			createdAt: identities.reduce<string | null>((best, u) => (u.createdAt && (!best || time(u.createdAt) < time(best)) ? u.createdAt : best), null),
			lastPasswordReset: resets.sort((a, b) => time(b) - time(a))[0] ?? null,
			hasPassword: identities.some((u) => u.hasPassword !== false),
			loginsCount: logins.length ? logins.reduce((s, n) => s + n, 0) : null,
			lastIp: identities.find((u) => !!u.lastIp)?.lastIp ?? null,
			emailVerified: identities.some((u) => u.emailVerified === true) ? true : identities.some((u) => u.emailVerified === false) ? false : null,
			source: primary.source,
		});
	}
	return persons;
}

// ------------------------------------------------------------------ impact des rôles

export type UserImpact = {
	/** Super-administrateur ou administrateur : tout, outillage compris. */
	admin: boolean;
	/** Peut mettre du contenu en PRODUCTION (outillage de déploiement ou verbe promote sur au moins un type). */
	production: boolean;
	/** Peut publier en preview (verbe publish sur au moins un type), sans forcément la production. */
	publish: boolean;
	/** Nombre de types de contenu que la personne peut mettre en production. */
	promoteTypes: number;
	/** Nombre de types de contenu que la personne peut écrire. */
	writeTypes: number;
};

const ADMIN_ROLE_KEYS = new Set(["bcc-admin", "administrator"]);

/**
 * Impact d'un jeu de rôles : lu dans les grants BRUTS (jokers) et EXPANSÉS des rôles. Sans
 * catalogue (pas encore chargé), seuls les jokers et les clés d'admin sont reconnus.
 */
export function impactOfRoles(roleKeys: string[], rolesByKey: Map<string, AssignableRole>, catalog: PermissionCatalog | null): UserImpact {
	const raw: string[] = [];
	let admin = false;
	for (const key of roleKeys ?? []) {
		if (ADMIN_ROLE_KEYS.has(key.trim().toLowerCase())) admin = true;
		const role = rolesByKey.get(key);
		for (const g of role?.permissions ?? []) raw.push((g ?? "").trim());
	}
	if (raw.includes("*") || raw.includes("feature:*:write")) admin = true;
	const deploymentTooling = admin || raw.includes("feature:deployment:write");
	if (!catalog) {
		const promoteRaw = raw.some((g) => g.endsWith(":promote"));
		const publishRaw = raw.some((g) => g.endsWith(":publish") || g.endsWith(":promote"));
		return { admin, production: deploymentTooling || promoteRaw, publish: deploymentTooling || publishRaw, promoteTypes: 0, writeTypes: 0 };
	}
	const expanded = expandGrants(catalog, raw);
	let promoteTypes = 0;
	let writeTypes = 0;
	let publishAny = false;
	for (const t of expanded) {
		if (!t.startsWith("content:")) continue;
		if (t.endsWith(":promote")) promoteTypes++;
		else if (t.endsWith(":publish")) publishAny = true;
		else if (t.endsWith(":write")) writeTypes++;
	}
	if (deploymentTooling) promoteTypes = Math.max(promoteTypes, catalog.contentTypes.length);
	return {
		admin,
		production: deploymentTooling || promoteTypes > 0,
		publish: deploymentTooling || publishAny || promoteTypes > 0,
		promoteTypes,
		writeTypes,
	};
}

// ------------------------------------------------------------------ filtres de la barre de recherche

export type UserFilterKey = "all" | "admins" | "production" | "contributors" | "blocked";

export const USER_FILTERS: Array<{ key: UserFilterKey; label: string; hint: string }> = [
	{ key: "all", label: "Tous", hint: "Tous les comptes actifs" },
	{ key: "admins", label: "Admins", hint: "Super-administrateurs et administrateurs" },
	{ key: "production", label: "Production", hint: "Comptes pouvant mettre du contenu en production (admins, responsables, déploiement)" },
	{ key: "contributors", label: "Contributeurs", hint: "Comptes qui écrivent sans pouvoir mettre en production" },
	{ key: "blocked", label: "Bloqués", hint: "Au moins une identité bloquée" },
];

export function matchesUserFilter(filter: UserFilterKey, person: UserPerson, impact: UserImpact): boolean {
	switch (filter) {
		case "admins":
			return impact.admin;
		case "production":
			return impact.production;
		case "contributors":
			return !impact.production && person.roles.length > 0;
		case "blocked":
			return person.anyBlocked;
		default:
			return true;
	}
}

/** Libellé humain du fournisseur d'identité, dérivé du préfixe de l'id Auth0. */
export function providerOf(id: string): { key: string; label: string; social: boolean } {
	const prefix = (id.split("|")[0] || "").toLowerCase();
	switch (prefix) {
		case "auth0":
			return { key: prefix, label: "E-mail / mot de passe", social: false };
		case "google-oauth2":
			return { key: prefix, label: "Google", social: true };
		case "windowslive":
			return { key: prefix, label: "Microsoft", social: true };
		case "facebook":
			return { key: prefix, label: "Facebook", social: true };
		case "apple":
			return { key: prefix, label: "Apple", social: true };
		case "linkedin":
			return { key: prefix, label: "LinkedIn", social: true };
		default:
			return { key: prefix || "local", label: prefix ? prefix : "Base locale", social: false };
	}
}
